import { useEffect, useState } from 'react'
import api from '../api/client'
import Modal from './Modal'
import Boton from './Boton'

/**
 * Modal "Pedir NC / Devolución" (Martin, 15 ago 2026 — ver
 * docs/diseno-nc-devolucion.md).
 *
 * El circuito: LGR NO emite la NC — la emite un humano en el desktop de
 * Presea. El modal (1) muestra qué va a pasar y qué emitir, (2) escala a
 * administración si la mercadería está en la calle o entregada, (3) recibe
 * el/los número(s) del comprobante emitido y los valida contra Presea
 * (DETALLE_COMPRO: existe, acredita, mismo cliente, cubre el importe con
 * percepciones, no usado por otra venta), y recién con ese OK refleja:
 * NV LIBERADA en borrador + remitos anulados + decisión del recibo.
 *
 * Recibos: Presea solo ANULA recibos DEL DÍA (el formulario pasa a
 * "**ANUL. RECIBO"); pasado el día se emite un recibo NEGATIVO. El modal
 * ofrece la opción correcta según la fecha de cada cobro.
 */
export default function NcDevolucionModal({ nvId, onCerrar, onHecho, esAdmin }) {
  const [pre, setPre] = useState(null)
  const [cargando, setCargando] = useState(true)
  const [numeros, setNumeros] = useState('')
  const [motivo, setMotivo] = useState('')
  const [decision, setDecision] = useState(null)
  const [numeroContra, setNumeroContra] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [aviso, setAviso] = useState(null)
  const [resultado, setResultado] = useState(null)

  const cargarPre = () => {
    setCargando(true)
    api.get(`/notas-venta/${nvId}/nc/prechequeo`)
      .then(({ data }) => setPre(data))
      .catch(() => setAviso({ ok: false, texto: 'No se pudo cargar el estado del pedido.' }))
      .finally(() => setCargando(false))
  }

  useEffect(cargarPre, [nvId])

  const tieneCobros = (pre?.cobros?.length ?? 0) > 0
  const aprobacionPendiente = pre?.requiere_aprobacion && pre?.aprobacion?.estado === 'pendiente'
  const aprobacionFalta = pre?.requiere_aprobacion && pre?.aprobacion?.estado !== 'aprobada'
  const hayRecibosDeHoy = pre?.cobros?.some(c => c.emitido_hoy)
  const hayRecibosViejos = pre?.cobros?.some(c => !c.emitido_hoy)

  const solicitarAprobacion = () => {
    setEnviando(true)
    setAviso(null)
    api.post(`/notas-venta/${nvId}/nc/solicitar-aprobacion`)
      .then(({ data }) => { setAviso({ ok: true, texto: data.mensaje }); cargarPre() })
      .catch(e => setAviso({ ok: false, texto: e.response?.data?.mensaje || 'No se pudo pedir la aprobación.' }))
      .finally(() => setEnviando(false))
  }

  const resolverAprobacion = (accion) => {
    setEnviando(true)
    setAviso(null)
    api.post(`/notas-venta/${nvId}/nc/${accion}`)
      .then(() => cargarPre())
      .catch(e => setAviso({ ok: false, texto: e.response?.data?.mensaje || 'No se pudo resolver.' }))
      .finally(() => setEnviando(false))
  }

  const registrar = () => {
    if (!numeros.trim()) {
      setAviso({ ok: false, texto: 'Pegá el número de la NC o devolución emitida en Presea.' })
      return
    }
    if (tieneCobros && !decision) {
      setAviso({ ok: false, texto: 'Elegí qué pasa con el recibo antes de registrar.' })
      return
    }
    setEnviando(true)
    setAviso(null)
    api.post(`/notas-venta/${nvId}/nc/registrar`, {
      numeros: numeros.trim(),
      motivo: motivo.trim() || undefined,
      decision_recibo: decision || undefined,
      numero_contra: numeroContra.trim() || undefined,
    })
      .then(({ data }) => { setResultado(data); onHecho?.() })
      .catch(e => setAviso({ ok: false, texto: e.response?.data?.mensaje || 'No se pudo registrar.' }))
      .finally(() => setEnviando(false))
  }

  return (
    <Modal title="🧾 Pedir NC / Devolución" onClose={onCerrar} maxWidth="640px">
      {cargando && <div style={s.dim}>Cargando…</div>}

      {!cargando && resultado && (
        <div>
          <div style={{ ...s.banner, background: '#f0fdf4', borderColor: '#86efac', color: '#166534' }}>
            ✓ {resultado.mensaje}
          </div>
          {(resultado.notas_credito || []).map(nc => (
            <div key={nc.numero} style={s.linea}>
              · {nc.formulario} <span style={s.mono}>{nc.numero}</span> — ${Number(nc.importe).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
            </div>
          ))}
          {(resultado.recibo?.acciones || []).map((a, i) => <div key={i} style={s.linea}>✓ {a}</div>)}
          {(resultado.recibo?.pendientes || []).map((p, i) => (
            <div key={i} style={{ ...s.linea, color: '#92400e' }}>⏳ {p}</div>
          ))}
          <div style={{ marginTop: '1rem', textAlign: 'right' }}>
            <Boton variante="primary" onClick={onCerrar}>Listo</Boton>
          </div>
        </div>
      )}

      {!cargando && !resultado && pre && (
        <div>
          {!pre.puede_pedir && (
            <div style={{ ...s.banner, background: '#fef2f2', borderColor: '#fecaca', color: '#b91c1c' }}>
              Este pedido no tiene una factura procesada para anular.
            </div>
          )}

          {/* ── Escalación a administración ─────────────────────────────── */}
          {aprobacionFalta && (
            <div style={{ ...s.banner, background: '#fffbeb', borderColor: '#fcd34d', color: '#92400e' }}>
              <div style={{ fontWeight: 600 }}>⚠ La mercadería está en distribución o entregada</div>
              <div style={{ marginTop: 4 }}>
                La anulación necesita la aprobación de administración antes de avanzar.
              </div>
              <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {!aprobacionPendiente && (
                  <Boton variante="primary" sm onClick={solicitarAprobacion} disabled={enviando}>
                    Pedir aprobación a administración
                  </Boton>
                )}
                {aprobacionPendiente && !esAdmin && (
                  <span style={{ fontSize: '0.8rem' }}>⏳ Esperando la aprobación de administración…</span>
                )}
                {aprobacionPendiente && esAdmin && (
                  <>
                    <Boton variante="success" sm onClick={() => resolverAprobacion('aprobar')} disabled={enviando}>✓ Aprobar</Boton>
                    <Boton variante="destructive" sm onClick={() => resolverAprobacion('rechazar')} disabled={enviando}>✕ Rechazar</Boton>
                  </>
                )}
              </div>
            </div>
          )}
          {pre.aprobacion?.estado === 'rechazada' && (
            <div style={{ ...s.banner, background: '#fef2f2', borderColor: '#fecaca', color: '#b91c1c' }}>
              ✕ Administración RECHAZÓ la anulación{pre.aprobacion.motivo ? ` — ${pre.aprobacion.motivo}` : ''}. Se puede volver a pedir si cambió la situación.
            </div>
          )}

          {/* ── Qué va a pasar ──────────────────────────────────────────── */}
          <div style={s.seccionTitulo}>Qué va a pasar al validar</div>
          {(pre.plan || []).map(p => (
            <div key={p.orden} style={s.linea}>· {p.descripcion}</div>
          ))}
          <div style={s.linea}>· La NV queda LIBERADA en borrador (para rehacer o archivar).</div>

          {/* ── Instrucción del desktop ─────────────────────────────────── */}
          <div style={{ ...s.banner, background: '#eff6ff', borderColor: '#bfdbfe', color: '#1e40af', marginTop: '0.75rem' }}>
            {pre.instruccion}
          </div>

          <input
            value={numeros}
            onChange={e => setNumeros(e.target.value)}
            placeholder="Nº del comprobante emitido (varias NC: separadas por coma)"
            style={s.input}
          />
          <input
            value={motivo}
            onChange={e => setMotivo(e.target.value)}
            placeholder="Motivo (queda en el registro de la NC)"
            style={{ ...s.input, fontFamily: 'inherit' }}
          />

          {/* ── Decisión del recibo ─────────────────────────────────────── */}
          {tieneCobros && (
            <div style={{ marginTop: '0.9rem' }}>
              <div style={s.seccionTitulo}>La venta tiene cobros — ¿qué pasa con el recibo?</div>
              {pre.cobros.map(c => (
                <div key={c.id} style={s.linea}>
                  · Recibo <span style={s.mono}>{c.numero_presea || `#${c.id}`}</span> — ${Number(c.importe).toLocaleString('es-AR', { minimumFractionDigits: 2 })} ({c.fecha}{c.emitido_hoy ? ' — DE HOY' : ''})
                </div>
              ))}

              <label style={s.radio}>
                <input type="radio" name="decision" checked={decision === 'anular'} onChange={() => setDecision('anular')} disabled={!hayRecibosDeHoy} />
                <span>
                  <b>Anularlo en el desktop</b> — solo recibos DE HOY (Presea no anula pasado el día).
                  {!hayRecibosDeHoy && <span style={s.dim}> No hay recibos de hoy.</span>}
                </span>
              </label>
              <label style={s.radio}>
                <input type="radio" name="decision" checked={decision === 'negativo'} onChange={() => setDecision('negativo')} disabled={!hayRecibosViejos && hayRecibosDeHoy} />
                <span><b>Recibo negativo</b> — para recibos de días anteriores: se emite en el desktop un recibo por el importe EN NEGATIVO.</span>
              </label>
              {decision === 'negativo' && (
                <input
                  value={numeroContra}
                  onChange={e => setNumeroContra(e.target.value)}
                  placeholder="Nº del recibo negativo (si ya lo emitiste — si no, el espejo lo detecta mañana)"
                  style={{ ...s.input, marginLeft: '1.5rem', width: 'calc(100% - 1.5rem)' }}
                />
              )}
              <label style={s.radio}>
                <input type="radio" name="decision" checked={decision === 'nada'} onChange={() => setDecision('nada')} />
                <span><b>Nada</b> — el cobro queda A CUENTA del cliente (imputable a una próxima compra).</span>
              </label>
            </div>
          )}

          {aviso && (
            <div style={{ marginTop: '0.6rem', fontSize: '0.8rem', color: aviso.ok ? '#166534' : '#b91c1c' }}>
              {aviso.ok ? '✓ ' : '✕ '}{aviso.texto}
            </div>
          )}

          <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Boton variante="ghost" onClick={onCerrar}>Cancelar</Boton>
            <Boton
              variante="primary"
              onClick={registrar}
              disabled={enviando || !pre.puede_pedir || aprobacionFalta}
            >
              {enviando ? 'Validando contra Presea…' : '✓ Validar y registrar'}
            </Boton>
          </div>
        </div>
      )}
    </Modal>
  )
}

const s = {
  banner: { padding: '0.7rem 0.9rem', border: '1px solid', borderRadius: 8, fontSize: '0.82rem', lineHeight: 1.5, marginBottom: '0.6rem' },
  seccionTitulo: { fontWeight: 700, fontSize: '0.8rem', color: '#334155', textTransform: 'uppercase', letterSpacing: '0.03em', margin: '0.6rem 0 0.35rem' },
  linea: { fontSize: '0.82rem', color: '#475569', lineHeight: 1.6 },
  mono: { fontFamily: 'ui-monospace, monospace' },
  dim: { fontSize: '0.8rem', color: '#94a3b8' },
  input: { width: '100%', boxSizing: 'border-box', marginTop: '0.6rem', padding: '0.5rem 0.7rem', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: '0.85rem', fontFamily: 'ui-monospace, monospace' },
  radio: { display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.82rem', color: '#334155', margin: '0.45rem 0', cursor: 'pointer', lineHeight: 1.5 },
}
